import axios from 'axios';
import { APIConfigData } from 'core/config/APIConfigData';
import { samplersConfig } from 'core/config/samplersConfig';
import { Samplers } from 'core/constants/enums/SamplersEnum.ts';
import { PromptsStatus } from 'core/constants/enums/PromptsStatus.ts';
import {
	formatPublicProfileData,
	getAspectRatio,
	getAspectRatioType,
	getErrorImgForAspectRatio,
	getHeightWidthFromAspectRatio,
} from 'core/utils/utils';
import { apiKeyEndpoints } from 'api/rtk-endpoints/ApiKeyEndpoints.ts';
import apiHandler from 'api/handlers/apiHandler.ts';
import { client } from 'api/baseApi.ts';
import ActivePlansResponse from 'api/response/ActivePlansResponse.ts';

export const getPublicProfile = (user) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/user/profile/${user}`,
			})
				.then((res) => {
					const { data } = res.data;

					resolve(formatPublicProfileData(data));
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getUserPromptHistoryService = (
	pageNumber,
	pageSize = APIConfigData.USER_PROMPT_HISTORY_PAGE_SIZE,
) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt/user`,
				params: {
					page: pageNumber,
					page_size: pageSize,
				},
				headers: { 'Content-Type': 'text/plain' },
			})
				.then((res) => {
					const { data: prompts, pagination } = res.data;
					const { total: listSize } = pagination;

					const formattedPrompts = {};

					prompts.map((promptObj) => {
						const {
							prompt_id: promptId,

							title: promptLabel,

							sampler,
							steps,
							cfg_scale: guidance,

							prompt,
							negative_prompt: negativePrompt,
							seed,

							model: modelObj,

							image,
							aspect_ratio: aspectRatio,

							creator,

							likes,
							liked,
							status,
							thumbnail_image: thumbnailImage,
						} = promptObj;
						const { handle: creatorHandle, image: creatorImage, name: creatorName } = creator;
						const { name: modelLabel, sd_model: model } = modelObj;

						const { height, width } = getHeightWidthFromAspectRatio(aspectRatio);

						formattedPrompts[promptId] = {
							id: promptId,

							promptLabel,

							modelLabel,
							model,

							sampler,
							steps,
							guidance,

							prompt,
							negativePrompt,
							seed,
							image,

							aspectRatio: getAspectRatio(width / height),
							aspectRatioType: getAspectRatioType(width / height),
							height,
							width,

							creatorHandle,
							creatorImage,
							creatorName,

							likes,
							liked,
							status,
							thumbnailImage,
						};

						return null;
					});

					resolve({ prompts: formattedPrompts, listSize, pageSize });
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getListPromptsHistory = (
	page,
	filterValue,
	filterBy = 'user',
	pageSize = APIConfigData.USER_PROMPT_HISTORY_PAGE_SIZE,
) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt`,
				headers: { 'Content-Type': 'text/plain' },
				params: {
					page,
					page_size: pageSize,
					filter_by: filterBy,
					filter_value: filterValue,
				},
			})
				.then((res) => {
					const { data: prompts, pagination } = res.data;
					const { total: listSize } = pagination;

					const formattedPrompts = {};

					prompts.map((promptObj) => {
						const {
							prompt_id: promptId,

							title: promptLabel,

							sampler,
							steps,
							cfg_scale: guidance,

							prompt,
							negative_prompt: negativePrompt,
							seed,

							model: modelObj,

							image,
							aspect_ratio: aspectRatio,

							creator,

							likes,
							liked,
							status = PromptsStatus.UNKNOWN,
							thumbnail_image: thumbnailImage,
						} = promptObj;
						const { handle: creatorHandle, image: creatorImage, name: creatorName } = creator;
						const { name: modelLabel, sd_model: model } = modelObj;

						const { height, width } = getHeightWidthFromAspectRatio(aspectRatio);

						formattedPrompts[promptId] = {
							id: promptId,

							promptLabel,

							modelLabel,
							model,

							sampler,
							steps,
							guidance,

							prompt,
							negativePrompt,
							seed,
							image,

							aspectRatio: getAspectRatio(width / height),
							aspectRatioType: getAspectRatioType(width / height),
							height,
							width,

							creatorHandle,
							creatorImage,
							creatorName,

							likes,
							liked,
							status,
							thumbnailImage,
						};

						return null;
					});

					resolve({ prompts: formattedPrompts, listSize, pageSize });
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getStudioGenerationHistoryService = (
	pageNumber,
	pageSize = APIConfigData.PROFILE_STUDIO_HISTORY_PAGE_SIZE,
) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'GET',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/images/studio/history`,
				params: {
					page: pageNumber,
					page_size: pageSize,
					type: 'studio',
				},
				headers: { 'Content-Type': 'text/plain' },
			})
				.then((res) => {
					const { data: history = [], pagination } = res.data;
					const { total: listSize = pageSize } = pagination;

					const finalHistory = [];
					history.map((batch) => {
						const {
							image_id: imageId,
							prompt_id: promptId,

							created_at: created,

							is_segment: isChangeIt,
							is_true_touch: isTrueTouch,

							prompt,
							negative_prompt: negativePrompt,
							model: generationModel,
							style: generationStyle,
							settings,
							seed: generationSeed,

							attributes,

							output,
							status,
							published,
							thumbnail,
						} = batch;
						const { name: model, preview_image: modelImage, label: modelLabel } = generationModel;

						const { id: styleId, preview_image: styleImage, name: styleName } = generationStyle;

						const {
							steps,
							cfg_scale: cfgScale,
							sampler: samplerIndex,
							size: imageSize,

							quality,
						} = settings;
						const samplerKey = Object.values(Samplers).find(
							(sKey) => samplersConfig[sKey]?.name === samplerIndex,
						);

						const { height, width } = imageSize;
						const aspectRatio = getAspectRatio(width / height);
						const aspectRatioType = getAspectRatioType(width / height);

						let controlNetSettings = null;
						let facelockSettings = null;
						if (attributes) {
							const { control_net: controlNet, facelock } = attributes;
							if (controlNet) {
								const {
									control_mode: controlMode,
									input_image: controlNetInputImage,
									resize_mode: resizeMode,
									nonsdxl,
									sdxl,
								} = controlNet;
								controlNetSettings = {
									inputImage: controlNetInputImage,
									controlMode,
									resizeMode,
									nonsdxl,
									sdxl,
								};
							}
							if (facelock) {
								const { input_image: facelockInputImage } = facelock;
								facelockSettings = {
									inputImage: facelockInputImage,
								};
							}
						}

						const historyItem = {
							loading: false,

							date: created,

							styleId,
							styleLabel: styleName,
							styleImage,

							isChangeIt,
							isTrueTouch,

							model,
							modelLabel,
							modelImage,

							sampler: samplerKey,
							steps,
							guidance: cfgScale,

							prompt,
							negativePrompt,

							aspectRatio,
							aspectRatioType,
							height,
							width,

							quality,

							published,

							attributes: {
								controlNet: controlNetSettings,
								facelock: facelockSettings,
							},
							promptId,
							thumbnail,
						};

						const generationLoading = status === 'initiated' || status === 'pending';
						const generationSuccess = status === 'success';
						const imageSrc = generationSuccess ? output : getErrorImgForAspectRatio(aspectRatio);
						const seed = generationSuccess ? generationSeed : -1;

						finalHistory.push({
							...historyItem,
							loading: generationLoading,
							seed,
							generationId: imageId,
							promptId,
							image: imageSrc,
							published,
						});

						return null;
					});

					const formattedRes = {
						history: finalHistory,
						listSize,
						pageSize,
					};

					resolve(formattedRes);
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const getActivePlansService = async () => {
	try {
		const responseData = await client.subscription.activePlan();
		const { data } = responseData;
		return new ActivePlansResponse(data).formatData();
	} catch (error) {
		if (!error?.code === 'ERR_CANCELED') throw error.response?.data;
		throw error?.response?.data;
	}
};

export const unpublishPrompt = (promptId) =>
	new Promise((resolve, reject) => {
		try {
			axios({
				method: 'DELETE',
				url: `${APIConfigData.RENDERNET_BASE_URL}/v1/prompt/${promptId}/archive`,
				headers: { 'Content-Type': 'text/plain' },
			})
				.then((res) => {
					const { data } = res.data;

					resolve(data);
				})
				.catch((error) => {
					reject(error?.response?.data?.err);
				});
		} catch (error) {
			reject(new Error(error.message));
		}
	});

export const resetApiKey = async ({ dispatch }) => {
	return apiHandler({
		endpoint: apiKeyEndpoints.endpoints.resetApiKey.initiate,
		dispatch,
	});
};

export const generateApiKey = async ({ dispatch }) => {
	return apiHandler({
		endpoint: apiKeyEndpoints.endpoints.generateApiKey.initiate,
		dispatch,
	});
};
